import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type Profile = { id: string; display_name: string | null; units: 'kg' | 'lb' };

type AuthApi = {
  session: Session | null;
  loading: boolean;
  profile: Profile | null;
  units: 'kg' | 'lb';
  signIn(email: string, password: string): Promise<string | null>;
  signUp(email: string, password: string, displayName?: string): Promise<{ error: string | null; needsConfirmation: boolean }>;
  signOut(): Promise<void>;
  setUnits(u: 'kg' | 'lb'): Promise<void>;
};

const Ctx = createContext<AuthApi | null>(null);

export function friendly(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials')) return 'Email or password is incorrect.';
  if (m.includes('already registered') || m.includes('already exists')) return 'An account with this email already exists. Sign in instead.';
  if (m.includes('email not confirmed')) return 'Confirm your email first: open the link we sent you, then sign in.';
  if (m.includes('failed to fetch') || m.includes('network')) return 'Can’t reach GymTV’s server. Check your connection and try again.';
  if (m.includes('password') && m.includes('6')) return 'Use a password of at least 8 characters.';
  return message;
}

export function AuthProvider({ client, children }: { client: SupabaseClient; children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    let alive = true;
    client.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data } = client.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const userId = session?.user.id;
  useEffect(() => {
    if (!userId) return setProfile(null);
    client
      .from('profiles')
      .select('id,display_name,units')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => setProfile((data as Profile) ?? null));
  }, [client, userId]);

  const api = useMemo<AuthApi>(
    () => ({
      session,
      loading,
      profile,
      units: profile?.units ?? 'kg',
      async signIn(email, password) {
        const { error } = await client.auth.signInWithPassword({ email: email.trim(), password });
        return error ? friendly(error.message) : null;
      },
      async signUp(email, password, displayName) {
        if (password.length < 8) return { error: 'Use a password of at least 8 characters.', needsConfirmation: false };
        const { data, error } = await client.auth.signUp({
          email: email.trim(),
          password,
          options: { data: displayName ? { display_name: displayName } : undefined, emailRedirectTo: window.location.origin },
        });
        if (error) return { error: friendly(error.message), needsConfirmation: false };
        return { error: null, needsConfirmation: !data.session };
      },
      async signOut() {
        await client.auth.signOut();
      },
      async setUnits(u) {
        if (!userId) return;
        setProfile((p) => (p ? { ...p, units: u } : p));
        await client.from('profiles').update({ units: u, updated_at: new Date().toISOString() }).eq('id', userId);
      },
    }),
    [client, session, loading, profile, userId],
  );
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}
